// src/components/MusteriDetay.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where, orderBy, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Box, Paper, Typography, CircularProgress, Grid, Button, Divider, IconButton, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert } from '@mui/material';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MessageIcon from '@mui/icons-material/Message'; // YENİ: SMS İkonu
import VehicleFormModal from '../components/VehicleFormModal';
import CustomerFormModal from '../components/CustomerFormModal';

const WorkOrderRow = ({ wo, onPaymentClick }) => (
    // ... (Bu kısım aynı) ...
    <Paper
        component={RouterLink}
        to={`/is-emri/${wo.id}`}
        variant="outlined"
        sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, textDecoration: 'none', color: 'inherit', '&:hover': { backgroundColor: 'action.hover' } }}
    >
        <Box>
            <Typography variant="body1" fontWeight="bold">{wo.aracPlaka} - {wo.aracMarkaModel}</Typography>
            <Typography variant="caption" color="text.secondary">
                {wo.isEmriTarihi && typeof wo.isEmriTarihi.toDate === 'function' ? format(wo.isEmriTarihi.toDate(), 'dd/MM/yyyy') : '-'} - {wo.durum}
            </Typography>
        </Box>
        <Box textAlign="right">
            <Typography variant="body1" fontWeight="bold">
                {(wo.toplamTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
            </Typography>
            {wo.kalanTutar > 0 ? (
                <Chip label={`Kalan: ${(wo.kalanTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}`} color="error" size="small" />
            ) : (
                <Chip label="Kapandı" color="success" size="small" />
            )}
        </Box>
    </Paper>
);

const MusteriDetay = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [smsLogs, setSmsLogs] = useState([]); // YENİ: SMS Kayıtları için state
    const [loading, setLoading] = useState(true);

    // Modallar
    const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

    useEffect(() => {
        setLoading(true);
        const customerDocRef = doc(db, "customers", id);

        const unsubCustomer = onSnapshot(customerDocRef, (doc) => {
            if (doc.exists()) {
                setCustomer({ id: doc.id, ...doc.data() });
            } else {
                navigate('/musteriler');
            }
        });

        const vehiclesQuery = query(collection(db, "vehicles"), where("musteriId", "==", id), orderBy("plaka", "asc"));
        const unsubVehicles = onSnapshot(vehiclesQuery, (snapshot) => {
            setVehicles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const workOrdersQuery = query(collection(db, "workOrders"), where("musteriId", "==", id), orderBy("isEmriTarihi", "desc"));
        const unsubWorkOrders = onSnapshot(workOrdersQuery, (snapshot) => {
            setWorkOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        // YENİ: SMS Geçmişini Çekme
        const smsQuery = query(collection(db, "smsLogs"), where("musteriId", "==", id), orderBy("tarih", "desc"));
        const unsubSms = onSnapshot(smsQuery, (snapshot) => {
            setSmsLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => {
            console.error("SMS geçmişi yüklenirken hata:", error);
            // Index hatası varsa, orderBy olmadan tekrar dene
            const fallbackQuery = query(collection(db, "smsLogs"), where("musteriId", "==", id));
            onSnapshot(fallbackQuery, (snapshot) => {
                const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                logs.sort((a, b) => (b.tarih?.toMillis?.() || 0) - (a.tarih?.toMillis?.() || 0));
                setSmsLogs(logs);
            });
        });

        return () => {
            unsubCustomer();
            unsubVehicles();
            unsubWorkOrders();
            unsubSms();
        };
    }, [id, navigate]);

    // ... (İş emirlerini ayırma ve diğer fonksiyonlar aynı) ...
    const { pendingWorkOrders, paidWorkOrders } = useMemo(() => {
        const realWorkOrders = workOrders.filter(wo => wo.aracPlaka !== "MANUEL BORÇ");
        const pending = realWorkOrders.filter(wo => wo.kalanTutar > 0);
        const paid = realWorkOrders.filter(wo => wo.kalanTutar <= 0);
        return { pendingWorkOrders: pending, paidWorkOrders: paid };
    }, [workOrders]);

    // G5 (2026-08-29): "Hızlı Tahsilat" ve "Borç Ekle" kaldırıldı — tahsilat YALNIZ
    // iş emri detayından girilir. Legacy transactions kayıtları ekstrede okunmaya devam eder.

    // ACİL DÜZELTME (2026-08-29): önceki sürümde onUpdateCustomer boş fonksiyona bağlıydı
    // (sessiz veri kaybı). Musteriler.js'teki handleUpdateCustomer deseniyle gerçek updateDoc.
    // Hata YUTULMAZ: throw edilir ki CustomerFormModal açık kalıp hata mesajı göstersin.
    const handleUpdateCustomer = async (customerId, updatedData) => {
        const customerDocRef = doc(db, 'customers', customerId);
        await updateDoc(customerDocRef, updatedData);
        alert("Müşteri bilgileri başarıyla güncellendi.");
    };

    // Hata YUTULMAZ: başarısız kayıtta modal açık kalıp gerçek hatayı göstermeli.
    const handleAddVehicle = async (vehicleData) => {
        await addDoc(collection(db, "vehicles"), { ...vehicleData, eklenmeTarihi: serverTimestamp(), createdAt: serverTimestamp() });
        alert("Araç eklendi!");
    };

    if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
    if (!customer) return <Typography>Müşteri bulunamadı.</Typography>;

    const renderWorkOrderList = (orders) => {
        if (orders.length === 0) return <Typography color="text.secondary" sx={{ p: 2 }}>İşlem bulunmuyor.</Typography>;
        return orders.map(wo => <WorkOrderRow key={wo.id} wo={wo} />);
    };

    const currentBalance = customer.toplamBakiye || 0;

    return (
        <Box>
            <VehicleFormModal isOpen={isVehicleModalOpen} onClose={() => setIsVehicleModalOpen(false)} onAddVehicle={handleAddVehicle} preselectedCustomer={customer} />
            <CustomerFormModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} customerToEdit={customer} onUpdateCustomer={handleUpdateCustomer} />

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3, position: 'relative' }}>
                            <IconButton sx={{ position: 'absolute', top: 8, right: 8 }} onClick={() => setIsCustomerModalOpen(true)}><EditIcon /></IconButton>
                            <Typography variant="h5" component="h1" gutterBottom>{customer.adSoyad}</Typography>
                            <Stack spacing={1.5} sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><PhoneIcon color="action" /><Typography>{customer.telefon || 'Belirtilmemiş'}</Typography></Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><EmailIcon color="action" /><Typography>{customer.email || 'Belirtilmemiş'}</Typography></Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><LocationOnIcon color="action" /><Typography>{customer.adres || 'Belirtilmemiş'}</Typography></Box>
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6">Araçları ({vehicles.length})</Typography>
                                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => setIsVehicleModalOpen(true)}>Araç Ekle</Button>
                            </Stack>
                            <Stack spacing={1}>
                                {vehicles.map(vehicle => (
                                    <Paper key={vehicle.id} variant="outlined" component={RouterLink} to={`/arac/${vehicle.id}`} sx={{ p: 1.5, textDecoration: 'none', color: 'inherit', '&:hover': { backgroundColor: 'action.hover' } }}>
                                        <Typography fontWeight="bold">{vehicle.plaka}</Typography>
                                        <Typography variant="caption">{vehicle.marka} {vehicle.model} ({vehicle.yil || 'Yıl yok'})</Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="h6" gutterBottom>Cari Hesap Özeti</Typography>
                                <Button variant="contained" size="small" startIcon={<AccountBalanceWalletIcon />} component={RouterLink} to={`/cari-hesaplar/${id}`}>Cari Ekstreyi Gör</Button>
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={4}><Typography variant="body2" color="text.secondary">Toplam Satış</Typography><Typography variant="h6" fontWeight="bold">{(customer.toplamSatis || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography></Grid>
                                <Grid item xs={12} sm={4}><Typography variant="body2" color="text.secondary">Toplam Tahsilat</Typography><Typography variant="h6" fontWeight="bold" color="success.main">{(customer.toplamOdeme || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography></Grid>
                                <Grid item xs={12} sm={4}><Typography variant="body2" color="text.secondary">Genel Bakiye</Typography><Typography variant="h6" fontWeight="bold" color={currentBalance > 0 ? "error.main" : "inherit"}>{(currentBalance).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</Typography></Grid>
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                        Tahsilatlar <b>yalnız iş emri üzerinden</b> girilir: aşağıdaki "Vadeli Ödemeler"
                                        listesinden iş emrini açıp <b>Ödeme Ekle</b>'yi kullanın. İstisnai satış/borç için
                                        "Diğer" hizmetli yeni iş emri açın.
                                    </Alert>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper><Typography variant="h6" sx={{ p: 2 }}>Vadeli Ödemeler (Ödeme Bekleyenler)</Typography><Divider /><Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>{renderWorkOrderList(pendingWorkOrders)}</Box></Paper>
                        <Paper><Typography variant="h6" sx={{ p: 2 }}>Geçmiş İşlemler (Tamamlananlar)</Typography><Divider /><Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>{renderWorkOrderList(paidWorkOrders)}</Box></Paper>

                        {/* YENİ: SMS Gönderim Geçmişi */}
                        <Paper>
                            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MessageIcon color="action" />
                                <Typography variant="h6">SMS İletişim Geçmişi</Typography>
                            </Box>
                            <Divider />
                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead><TableRow><TableCell>Tarih</TableCell><TableCell>Mesaj</TableCell><TableCell>Durum</TableCell></TableRow></TableHead>
                                    <TableBody>
                                        {smsLogs.length > 0 ? smsLogs.map(sms => (
                                            <TableRow key={sms.id}>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{sms.tarih?.toDate().toLocaleString('tr-TR')}</TableCell>
                                                <TableCell>{sms.mesaj}</TableCell>
                                                <TableCell>
                                                    <Chip label={sms.basarili ? "İletildi" : "Hata"} color={sms.basarili ? "success" : "error"} size="small" />
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow><TableCell colSpan={3} align="center">Gönderilmiş SMS bulunmuyor.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MusteriDetay;